import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface TrendPoint {
  date: string;
  label: string;
  screens_produced: number;
  revenue: number;
  material_cost: number;
  labor_cost: number;
  overhead: number;
  true_cost: number;
  margin: number;
  margin_percent: number;
}

interface TrendLineChartProps {
  data: TrendPoint[] | null;
  loading: boolean;
  metric: 'cost' | 'margin' | 'volume';
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export function TrendLineChart({ data, loading, metric }: TrendLineChartProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Trends Over Time</h3>
        <div className="h-80 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-600" />
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Trends Over Time</h3>
        <div className="h-80 flex items-center justify-center text-gray-500">
          No trend data available
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          {payload.map((item: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: item.color }}>
              {item.name}: {
                item.name.includes('$') || item.name === 'Revenue' || item.name === 'Cost' || item.name === 'Margin'
                  ? formatCurrency(item.value)
                  : item.name === 'Margin %'
                  ? `${item.value.toFixed(1)}%`
                  : item.value.toLocaleString()
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const getTitle = () => {
    switch (metric) {
      case 'cost': return 'Cost Trends';
      case 'margin': return 'Margin Trends';
      case 'volume': return 'Production Volume';
      default: return 'Trends';
    }
  };

  const renderLines = () => {
    switch (metric) {
      case 'cost':
        return (
          <>
            <Line
              type="monotone"
              dataKey="material_cost"
              name="Material"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="labor_cost"
              name="Labor"
              stroke="#10B981"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="overhead"
              name="Overhead"
              stroke="#F59E0B"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="true_cost"
              name="Total Cost"
              stroke="#EF4444"
              strokeWidth={2}
              dot={false}
            />
          </>
        );
      case 'margin':
        return (
          <>
            <Line
              type="monotone"
              dataKey="revenue"
              name="Revenue"
              stroke="#10B981"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="margin"
              name="Margin"
              stroke="#3B82F6"
              strokeWidth={2}
              dot={false}
            />
          </>
        );
      case 'volume':
        return (
          <Line
            type="monotone"
            dataKey="screens_produced"
            name="Screens"
            stroke="#8B5CF6"
            strokeWidth={2}
            dot={false}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{getTitle()}</h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="label"
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
            />
            <YAxis
              stroke="#6B7280"
              fontSize={12}
              tickLine={false}
              tickFormatter={(value) =>
                metric === 'volume' ? value.toLocaleString() : formatCurrency(value)
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {renderLines()}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default TrendLineChart;
